import React from 'react';

import isArray from 'lodash/isArray';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiFileTreeOutline } from '@lumapps/lumx/icons';
import { SkeletonTypography, Text, Typography } from '@lumapps/lumx/react';
import { Metadata } from '@lumapps/metadata/types';
import { useTranslate } from '@lumapps/translations';

import { DrawerSection, DrawerSectionProps } from '../DrawerSection';

const SCOPE = 'lumx-drawer-metadatas-section';

export interface DrawerMetadatasSectionProps extends Omit<DrawerSectionProps, 'title'> {
    /** whether the metadatas are loading or not */
    isLoading?: boolean;
    /** list of parent metadatas to display */
    parentMetadatas: Partial<Metadata>[];
    /** list of selected metadata values for each metadata parent */
    selectedMetadatas: Record<string, Partial<Metadata> | Partial<Metadata>[]>;
    /** scope to use for data-ids purpose */
    scope?: string;
}

/**
 * Displays a list of metadatas inside the context of a Drawer
 * @family Drawer
 * @param DrawerMetadatasSectionProps
 * @returns DrawerMetadatasSection
 */
export const DrawerMetadatasSection: React.FC<DrawerMetadatasSectionProps> = ({
    selectedMetadatas,
    parentMetadatas,
    isLoading,
    scope = SCOPE,
    ...props
}) => {
    const { translateObject } = useTranslate();
    const { get } = useDataAttributes(scope);

    if (isLoading) {
        return (
            <DrawerSection
                title={<SkeletonTypography typography="overline" width="100px" />}
                icon={mdiFileTreeOutline}
                isLoading
                {...props}
            />
        );
    }

    return (
        <>
            {parentMetadatas.map((parentMetadata) => {
                const metadatas = selectedMetadatas[parentMetadata.id as string];

                if (metadatas) {
                    const metadataToUse = isArray(metadatas) ? metadatas : [metadatas];

                    if (metadataToUse.length > 0) {
                        return (
                            <DrawerSection
                                title={translateObject(parentMetadata.name) || ''}
                                key={parentMetadata.id}
                                icon={mdiFileTreeOutline}
                                {...props}
                                scope={`${scope}-metadatas-${parentMetadata.id}`}
                            >
                                <Text
                                    as="span"
                                    typography={Typography.body1}
                                    {...get({ element: 'metadatas', action: `${parentMetadata.id}-values` })}
                                >
                                    {metadataToUse?.map((metadata) => translateObject(metadata.name)).join(', ')}
                                </Text>
                            </DrawerSection>
                        );
                    }
                }

                return null;
            })}
        </>
    );
};
